import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { UserContext } from "../context/UserContext";
import Swal from "sweetalert2";
import FormularioArriendo from "./FormularioArriendo";
import "./ListadoArriendos.css";

const ListadoArriendos = () => {
    const { currentUser } = useContext(UserContext);
    const [arriendos, setArriendos] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState(2025);
    const [factoresLoaded, setFactoresLoaded] = useState(false);
    const [modalForm, setModalForm] = useState(false);
    const [selectedCliente, setSelectedCliente] = useState(null);
    const [search, setSearch] = useState("");
    const [showNuevoClienteForm, setShowNuevoClienteForm] = useState(false);

    const getAuthHeader = () => {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No token available');
        
        return {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        };
    };

    // Verificar factores del año
    const verificarFactores = async () => {
        console.log("Iniciando verificación de factores para año:", selectedYear);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/factores-arriendo/year/${selectedYear}`,
                getAuthHeader()
            );
            
            console.log("Datos recibidos de factores:", res.data);
            
            // Verificar si hay al menos un factor
            const hayFactores = Array.isArray(res.data) && res.data.length > 0;
            console.log("¿Hay factores?:", hayFactores, "Cantidad:", res.data.length);
            
            setFactoresLoaded(hayFactores);
            return hayFactores;
        } catch (error) {
            console.error("Error específico al verificar factores:", error.response || error);
            setFactoresLoaded(false);
            return false;
        }
    };

    // Cargar arriendos y clientes
    const fetchData = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.log('No token in localStorage');
            return;
        }
    
        setLoading(true);
        try {
            // Primero verificar si ya existe apertura para el año
            const aperturaRes = await axios.get(
                `${process.env.REACT_APP_API_URL}/arriendos?anio=${selectedYear}`,
                getAuthHeader()
            );
    
            // Si no hay apertura, solo verificar factores
            if (!aperturaRes.data?.arriendos?.length) {
                const factoresRes = await verificarFactores();
                setFactoresLoaded(factoresRes);
                setClientes([]); // No mostrar clientes si no hay apertura
                setArriendos([]);
            } else {
                // Si hay apertura, cargar los datos completos
                const clientesRes = await axios.get(
                    `${process.env.REACT_APP_API_URL}/listados?arriendo=true`,
                    getAuthHeader()
                );
                
                setFactoresLoaded(false); // Deshabilitar botón de apertura
                setClientes(clientesRes.data || []);
                setArriendos(aperturaRes.data.arriendos || []);
            }
        } catch (error) {
            console.error('Error al cargar datos:', error);
            setClientes([]);
            setArriendos([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.log('No token in localStorage');
            return;
        }

        if (!selectedYear) {
            console.log('No año available');
            return;
        }

        fetchData();
    }, [selectedYear]);


    const handleApertura = async () => {
        if (!factoresLoaded) {
            Swal.fire({
                icon: "warning",
                title: "Factores Incompletos",
                text: "Debe tener al menos un factor ingresado para el año"
            });
            return;
        }
    
        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/arriendos/apertura/${selectedYear}`,
                {},
                getAuthHeader()
            );
            Swal.fire({
                icon: "success",
                title: "Éxito",
                text: "Apertura realizada correctamente"
            });
            fetchData();
        } catch (error) {
            console.error("Error en apertura:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Error realizando la apertura"
            });
        }
    };

    // Cambiar estado de arriendo
    const handleCambiarEstado = async (id, nuevoEstado) => {
        try {
            await axios.put(
                `${process.env.REACT_APP_API_URL}/arriendos/${id}`,
                { estado: nuevoEstado },
                getAuthHeader()
            );
            fetchData();
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Error actualizando estado"
            });
        }
    };

    const filteredClientes = clientes.filter(
        c =>
            c.nombre.toLowerCase().includes(search.toLowerCase()) ||
            c.rut.includes(search)
    );

    return (
        <div className="listado-arriendos-container">
        <div className="header-section">
        <h2>Listado de Arriendos {selectedYear}</h2>
            <div>
                <p>Estado factores: {factoresLoaded ? 'Cargados' : 'No cargados'}</p>
                <button 
                    onClick={handleApertura}
                    disabled={!factoresLoaded}
                    className={`apertura-button ${!factoresLoaded ? 'disabled' : ''}`}
                >
                    Apertura de Arriendos {factoresLoaded ? '(Habilitado)' : '(Deshabilitado)'}
                </button>
            </div>
        </div>

        <div className="table-container">
            <table className="arriendos-table">
                <thead>
                    <tr>
                        <th>Cliente</th>
                        <th>RUT</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {clientes.map(cliente => (
                        <tr key={cliente._id}>
                            <td>{cliente.nombre}</td>
                            <td>{cliente.rut}</td>
                            <td>
                                {arriendos.find(a => a.clienteId === cliente._id)?.estado || 'Pendiente'}
                            </td>
                            <td>
                                <button
                                    onClick={() => {
                                        setSelectedCliente(cliente);
                                        setModalForm(true);
                                    }}
                                    className="action-button"
                                >
                                    Nuevo Arriendo
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>

        {modalForm && selectedCliente && (
            <div className="modal-overlay">
                <FormularioArriendo
                    cliente={selectedCliente}
                    año={selectedYear}
                    onClose={() => {
                        setModalForm(false);
                        fetchData();
                    }}
                    onSave={() => {
                        setModalForm(false);
                        fetchData();
                    }}
                />
            </div>
        )}
    </div>
);
};

export default ListadoArriendos;